import {
  getFallbackDefaultType,
  getIssueTypeEntitiesMap,
} from "@adsk/acc-issues-core/state/redux/ducks/issueTypes";
import {
  getCurrentUserAllTypesForNewIssue,
  getCurrentUserPermittedTypesForNewIssue,
} from "@adsk/acc-issues-core/state/redux/ducks/currentUserPermissions";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import { LocalStorageKeys } from "@adsk/acc-issues-core/state/localStorage";
import { useLocalStorage } from "@adsk/acc-issues-core/utils/hooks/useLocalStorage";
import { usePermittedGroupedTypes } from "@adsk/acc-issues-common/hooks/usePermittedGroupedTypes";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@adsk/acc-issues-core/utils/featureFlags";
import { IssueType } from "@adsk/acc-issues-types";

export const useDefaultType = (): IssueType | undefined => {
  const [getLastSelectedTypeId] = useLocalStorage(
    LocalStorageKeys.LAST_SELECTED_TYPE_ID
  );
  const lastSelectedTypeId = getLastSelectedTypeId();

  const issueEntitiesMap = useSelector(getIssueTypeEntitiesMap);
  const fallbackDefaultType = useSelector(getFallbackDefaultType);
  const selectedType =
    lastSelectedTypeId && issueEntitiesMap[lastSelectedTypeId];
  // The user's "catalog default" type: last-used-still-active type from local
  // storage, else the system's fallback default
  const catalogDefault =
    selectedType && selectedType.isActive ? selectedType : fallbackDefaultType;

  const { value: isVisibilityByTypeOn } = useFeatureFlag(
    FeatureFlag.VisibilityByType
  );
  const allTypes = useSelector(getCurrentUserAllTypesForNewIssue);
  const permittedTypeIds = useSelector(getCurrentUserPermittedTypesForNewIssue);
  const permittedGroupedTypes = usePermittedGroupedTypes();

  if (!catalogDefault) {
    return undefined;
  }

  // If there's a default, use it provided that one of the following is true:
  // 1. Visibility by type is off
  // 2. All types are allowed
  // 3. The default type is in the permitted types
  if (
    !isVisibilityByTypeOn ||
    allTypes ||
    permittedTypeIds.includes(catalogDefault.id)
  ) {
    return catalogDefault;
  }

  // If the default is not in the permitted types, return the first permitted
  // type or undefined if no permitted types.
  return permittedGroupedTypes[0]?.types[0];
};
